<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Procedury</span>
        <h3 class="page-title">Lista procedur</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="warning"
          @click.prevent="exportCosts()"
          :disabled="exportInProgress">
          <div
            v-if="exportInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-else>
            <i class="material-icons">download</i>
            Eksportuj koszty
          </template>
        </d-button>

        <d-button
          theme="primary"
          @click.prevent="openImportPopup()"
          class="ml-2">
          <i class="material-icons">upload</i>
          Importuj koszty
        </d-button>

        <d-button
          theme="success"
          class="ml-2"
          @click.prevent="$router.push('/procedury/nowa')">
          <i class="material-icons">add</i>
          Dodaj procedurę
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="procedures-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          placeholder="Podaj nazwę procedury lub procesu"
          v-model="filters.search" />

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Nazwa procedury</th>
            <th scope="col" class="border-0">Liczba procesów</th>
            <th scope="col" class="border-0">Liczba produktów</th>
            <th scope="col" class="border-0">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(procedure, index) of procedures"
            :key="'procedure-' + index">
            <td class="align-middle">
              {{ procedure.name }}
            </td>
            <td class="align-middle">
              {{ procedure.processes_count }}
            </td>
            <td class="align-middle">
              {{ procedure.products_count }}
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/procedury/edycja/' + procedure.id)"
                class="mr-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeProcedure(procedure.id)"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="procedures.length === 0">
            <td
              colspan="5"
              class="text-center">
              Brak procedur do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <d-modal
      v-if="showImportPopup"
      @close="closeImportPopup">
      <d-modal-header>
        <d-modal-title>
          Importuj plik z kosztami procesów
        </d-modal-title>
      </d-modal-header>
      <d-modal-body>
        <d-form>
          <validation-observer ref="import-form" tag="form">
            <div class="form-group">
              <label for="name">
                Plik kosztów:
              </label>

              <validation-provider
                rules="required"
                name="file"
                v-slot="{ errors }">
                <file-input
                  :onChange="setNewFile"
                  id="inputFileImport" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </validation-observer>
        </d-form>
      </d-modal-body>
      <d-modal-footer>
        <d-button
          class="ml-0"
          theme="danger"
          :disabled="importInProgress"
          @click.prevent="closeImportPopup">
          Anuluj
        </d-button>
        <d-button
          class="ml-auto"
          theme="success"
          :disabled="importInProgress"
          @click.prevent="validateImportForm">
          <div
            v-if="importInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          Importuj
        </d-button>
      </d-modal-footer>
    </d-modal>
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import jsonToFormData from 'json-form-data';
import Vue from 'vue';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'procedures',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data() {
    return {
      procedures: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: ''
      },
      exportInProgress: false,
      importFile: null,
      importInProgress: false,
      showImportPopup: false
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      ListUtils.loadItemsData(this, {
        endpoint: '/procedures/items?page=' + this.currentPage,
        listField: 'procedures',
        pagination: true,
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: ''
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['procedures-filters-form'], this.debouncedLoadFilteredData);
    },
    removeProcedure (procedureID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/procedures/delete/',
        id: procedureID,
        successTitle: 'Usunięto procedurę',
        successText: 'Wybrana procedura została usunięta',
        confirmTitle: 'Czy na pewno chcesz usunąć tę procedurę?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie procedury nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    openImportPopup () {
      this.showImportPopup = true;
    },
    closeImportPopup () {
      this.importFile = null;
      this.importSize = null;
      this.showImportPopup = false;
    },
    validateImportForm () {
      FormUtils.validate(this.$refs['import-form'], this.importCosts);
    },
    setNewFile (event) {
      this.importFile = event.target.files[0];
    },
    importCosts () {
      this.importInProgress = true;

      let importData = {
        file: this.importFile
      };

      this.$http.request({
        method: 'POST',
        url: '/procedures/import-costs',
        headers: { 'Content-Type': undefined },
        transformRequest (data) {
          let dataToSend = jsonToFormData(data);
          return dataToSend;
        },
        data: importData
      }).then((response) => {
        if (response.data.success) {
          Vue.swal({
            title: 'Zaimportowano koszty procesów',
            html: 'Udało się pomyślnie zaimportować koszty procesów',
            icon: 'success',
            confirmButtonText: 'OK',
            buttonsStyling: true,
            timer: 2000
          });
        }

        this.closeImportPopup();
        this.clearFilters();
        this.importInProgress = false;
      }).catch(error => {
        console.log(error);
        this.closeImportPopup();

        Vue.swal({
          title: 'Import nie powiódł się',
          html: 'Operacja importu kosztów procesów nie powiodła się. Spróbuj ponownie...',
          icon: 'error',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.importInProgress = false;
      });
    },
    exportCosts () {
      this.exportInProgress = true;

      this.$http.post('/procedures/export-costs', {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'koszty-procesow.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.exportInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Eksport nie powiódł się',
          html: 'Wyeksportowanie kosztów procesów nie powiodło się. Spróbuj ponownie...',
          type: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.exportInProgress = false;
      });
    }
  }
};
</script>
